import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {SelectOptionConfiguration} from "../core/config/select-option-configuration";

@JsonObject()
export class ThresholdsConfiguration {

    /**
     * The <baseId - parameter type> combination the thresholds are available for
     *
     * @example "110-Abfluss"
     */
    @JsonProperty()
    declare parameterTypes: string[];

    /**
     * If thresholds should be stacked
     */
    @JsonProperty()
    declare stacked: boolean;

    /**
     * If thresholds should be displayed as areas
     */
    @JsonProperty()
    declare plane: boolean;

    /**
     * Labels for total values
     */
    @JsonProperty()
    declare values: SelectOptionConfiguration[];

    constructor() {}
}

@JsonObject()
export class TimeLocaleDefinitionConfiguration {
    /**
     * The date and time (%c) format specifier (e.g., "%a %b %e %X %Y").
     */
    @JsonProperty()
    declare dateTime: string;
    /**
     * The date (%x) format specifier (e.g., "%m/%d/%Y").
     */
    @JsonProperty()
    declare date: string;
    /**
     * The time (%X) format specifier (e.g., "%H:%M:%S").
     */
    @JsonProperty()
    declare time: string;
    /**
     * The A.M. and P.M. equivalents (e.g., ["AM", "PM"]).
     */
    @JsonProperty()
    declare periods: [string, string];
    /**
     * The full names of the weekdays, starting with Sunday.
     */
    @JsonProperty()
    declare days: [string, string, string, string, string, string, string];
    /**
     * The abbreviated names of the weekdays, starting with Sunday.
     */
    @JsonProperty()
    declare shortDays: [string, string, string, string, string, string, string];
    /**
     * The full names of the months (starting with January).
     */
    @JsonProperty()
    declare months: [string, string, string, string, string, string, string, string, string, string, string, string];
    /**
     * the abbreviated names of the months (starting with January).
     */
    @JsonProperty()
    declare shortMonths: [string, string, string, string, string, string, string, string, string, string, string, string];

    constructor() {}
}

@JsonObject()
export class ChartMarginConfiguration {
    @JsonProperty()
    declare top: number;

    @JsonProperty()
    declare right: number;

    @JsonProperty()
    declare bottom: number;

    @JsonProperty()
    declare left: number;

    constructor() {}
}

@JsonObject()
export class AxisLabelsConfiguration {

    /**
     * The parameter type the axis labels are applicable
     */
    @JsonProperty()
    declare parameterTypes: string[];

    /**
     * Labels for mean values
     */
    @JsonProperty()
    declare mean: object;

    /**
     * Labels for total values
     */
    @JsonProperty()
    declare total: object;

    constructor() {}
}

@JsonObject()
export class PeriodOptionsConfiguration {

    /**
     * The station parameter name the time period options are available for
     */
    @JsonProperty()
    declare stationParameterNames: string[];

    /**
     * available time period options
     */
    @JsonProperty()
    declare options: SelectOptionConfiguration[];

    constructor() {
    }
}


@JsonObject()
export class TimeFormatConfiguration {

    /**
     * The parameter type the time format is applicable
     */
    @JsonProperty()
    declare stationParameterNames: string[];

    /**
     * formats for each time period
     */
    @JsonProperty()
    declare formats: object;

    constructor() {}
}

@JsonObject()
export class AxisConfiguration {
    /**
     * Formats for tick labels, specified for each time period (ISO) available
     * See: https://github.com/d3/d3-time-format#locale_format
     */
    @JsonProperty()
    declare tickFormats: object;

    /**
     * Labels for Axis
     */
    @JsonProperty()
    declare labels: AxisLabelsConfiguration[];

    constructor() {}
}

@JsonObject()
export class AxesConfiguration {
    @JsonProperty()
    declare xAxis: AxisConfiguration;

    @JsonProperty()
    declare yAxis: AxisConfiguration;

    constructor() {}
}

@JsonObject()
export class TimeSeriesRangeSelectorConfiguration {

    /**
     * Show only period selector
     */
    @JsonProperty()
    declare onlyPeriod: boolean;

    constructor() {}
}

@JsonObject()
export class ChartConfiguration {
    // default time locale
    @JsonProperty({type: TimeLocaleDefinitionConfiguration})
    declare defaultTimeLocale: TimeLocaleDefinitionConfiguration;

    // initial width of the graph
    @JsonProperty()
    declare width: number;

    // initial height of the graph
    @JsonProperty()
    declare height: number;

    @JsonProperty()
    declare margin: ChartMarginConfiguration;

    /**
     * Margins when second axis is needed
     */
    @JsonProperty()
    declare marginTwoAxis: ChartMarginConfiguration;

    // available time periods
    @JsonProperty()
    declare periods: PeriodOptionsConfiguration[];

    // configuration for axes
    @JsonProperty()
    declare axes: AxesConfiguration;

    /**
     * Formatters for times in labels, depending on period an parameter type
     */
    @JsonProperty()
    declare timeFormats: TimeFormatConfiguration[];

    /**
     * Thresholds
     * Available thresholds depend on parameterType
     */
    @JsonProperty()
    declare thresholds: ThresholdsConfiguration[];

    /**
     * Types with bar charts
     * List of stationParameterNames
     */
    @JsonProperty()
    declare barCharts: string[];

    /**
     * Types with cumulative value curves
     */
    @JsonProperty()
    declare cumulativeValue: string[];

    /**
     * Configuration for time range selectors
     */
    @JsonProperty()
    declare rangeSelector: TimeSeriesRangeSelectorConfiguration;

    constructor() {}
}
